/**
 * Extraction Grouping Types
 *
 * Types for grouping documents in the Data Extraction tab,
 * mirroring the Encompass eFolder grouping system.
 */

import type { DocumentWithExtraction, EncompassAttachmentWithExtraction, ExtractionStatus } from '@/hooks/useExtractionData';

/**
 * Grouping mode for the extraction tab
 * - groups: Show documents grouped by category with collapsible sections
 * - flat: Show documents in a flat list (current behavior)
 */
export type ExtractionGroupingMode = 'groups' | 'flat';

/**
 * A group of documents with the same category
 */
export interface ExtractionDocumentGroup<T = DocumentWithExtraction | EncompassAttachmentWithExtraction> {
  /** Canonical group name from Encompass (e.g., "Income", "Asset", "Credit Report") */
  groupName: string;
  /** Formatted display name */
  displayName: string;
  /** Documents in this group */
  documents: T[];
  /** Total document count in this group */
  totalCount: number;
  /** Count of unprocessed documents */
  unprocessedCount: number;
  /** Count of documents currently processing (pending, extracting, classifying, splitting) */
  processingCount: number;
  /** Count of completed documents */
  completedCount: number;
  /** Count of failed documents */
  failedCount: number;
  /** Sort order (lower = first, 999 = last for Unclassified) */
  order: number;
}

/**
 * Statistics for a group of documents
 */
export interface GroupStats {
  totalCount: number;
  unprocessedCount: number;
  processingCount: number;
  completedCount: number;
  failedCount: number;
}

/**
 * Check if a status is considered "processing"
 */
export function isProcessingStatus(status: ExtractionStatus | string | null | undefined): boolean {
  if (!status) {
return false;
}
  return ['pending', 'extracting', 'extracted', 'classifying', 'classified', 'splitting'].includes(status);
}

/**
 * Check if a status is considered "completed"
 */
export function isCompletedStatus(status: ExtractionStatus | string | null | undefined): boolean {
  return status === 'completed';
}

/**
 * Check if a status is considered "failed"
 */
export function isFailedStatus(status: ExtractionStatus | string | null | undefined): boolean {
  return status === 'failed';
}

/**
 * Check if a status is considered "unprocessed"
 */
export function isUnprocessedStatus(status: ExtractionStatus | string | null | undefined): boolean {
  return !status || status === 'unprocessed';
}
